import { useState } from 'react'
import { MessageSquarePlus, Check } from 'lucide-react'
import { useI18n } from '../i18n'
import { casesAPI } from '../services/api'
import { useCaseStore } from '../stores'
import { formatDate } from '../utils/dateUtils'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * PatientNoteCard — v242.235.0 (session 96).
 *
 * ★★ Dr. Rami's mockup-B ruling: once the summary is written the answers are locked and
 *    "later changes go to the doctor as a note". This is that note: appended to the case
 *    (POST /cases/<id>/patient-notes, orella-api v243.279.0) and shown to the physician with
 *    the case. It never edits an answer and never touches the summary.
 */
export default function PatientNoteCard({ caseId, notes = [] }) {
  const { t } = useI18n()
  const updateCase = useCaseStore((s) => s.updateCase)
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const [justAdded, setJustAdded] = useState(false)

  const submit = async () => {
    const value = text.trim()
    if (!value || saving || !caseId) return
    setSaving(true)
    setError(false)
    try {
      const res = await casesAPI.addPatientNote(caseId, value)
      if (res && Array.isArray(res.notes)) {
        updateCase({ patientNotes: res.notes })
        setText('')
        setJustAdded(true)
        setTimeout(() => setJustAdded(false), 2500)
      } else {
        setError(true)
      }
    } catch (err) {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="px-4 pt-3.5">
      <div className="rounded-2xl bg-white px-4 py-3.5" style={{ border: '1.5px solid #A7D9FC' }}>
        <div className="flex items-center gap-2">
          <MessageSquarePlus size={17} style={{ color: '#46A8F9' }} />
          <span className="text-[15px] font-extrabold text-[#15354F]">{t('patientNote.title')}</span>
        </div>
        <p className="text-[13px] text-[#4A5A6B] leading-snug mt-1.5">{t('patientNote.sub')}</p>
        {notes.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8598A9]">{t('patientNote.yourNotes')}</span>
            {notes.map((n) => (
              <div key={n.id} className="rounded-xl bg-[#F6F9FC] px-3 py-2">
                <p className="text-[13.5px] text-[#15354F] whitespace-pre-wrap">{n.text}</p>
                {n.createdAt && <p className="text-[11px] text-[#8598A9] mt-1">{formatDate(n.createdAt)}</p>}
              </div>
            ))}
          </div>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 4000))}
          placeholder={t('patientNote.placeholder')}
          rows={3}
          className="mt-3 w-full rounded-xl px-3 py-2.5 text-[14px] text-[#15354F] outline-none resize-none"
          style={{ border: '1.5px solid #E5EDF3' }}
        />
        {error && <p className="text-[12.5px] text-[#B4232A] mt-1.5">{t('patientNote.failed')}</p>}
        <button
          onClick={submit}
          disabled={!text.trim() || saving}
          className="mt-2.5 w-full py-2.5 rounded-full text-[14px] font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: '#46A8F9' }}
        >
          {saving ? <OrellaSpinner size={16} tone="inherit" /> : justAdded ? <Check size={16} /> : null}
          {justAdded ? t('patientNote.added') : t('patientNote.add')}
        </button>
      </div>
    </div>
  )
}
